import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Layers, BookOpen, Shuffle, TrendingUp } from 'lucide-react';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';

interface Book {
  id: string;
  title: string;
  author: string;
  card_count: number;
  learned_count: number;
}

export default function Study() {
  const [, setLocation] = useLocation();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCards: 0,
    learnedCards: 0,
    todayCards: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await api.getBooks();
      const booksData = response.data || [];
      setBooks(booksData);

      // 计算统计数据
      const totalCards = booksData.reduce((sum: number, book: Book) => sum + (book.card_count || 0), 0);
      const learnedCards = booksData.reduce((sum: number, book: Book) => sum + (book.learned_count || 0), 0);
      
      setStats({
        totalCards,
        learnedCards,
        todayCards: 0, // TODO: 实现今日学习统计
      });
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRandomStudy = () => {
    // 随机复习所有卡片
    setLocation('/cards');
  };

  const handleBookStudy = (bookId: string) => {
    // 学习指定书籍的卡片
    setLocation(`/cards?bookId=${bookId}`);
  };

  return (
    <div className="min-h-screen bg-[#1a2e3e] pb-24">
      {/* 头部 */}
      <div className="p-6">
        <div className="flex items-center gap-3 mb-2">
          <Layers className="w-8 h-8 text-[#22a9d9]" />
          <h1 className="text-3xl font-bold text-white">刷知识</h1>
        </div>
        <p className="text-white/60">开始你的学习之旅</p>
      </div>

      {/* 统计卡片 */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-3 gap-3">
          {/* 总卡片数 */}
          <div className="glass rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold text-white mb-1">
              {stats.totalCards}
            </div>
            <div className="text-xs text-white/60">总卡片</div>
          </div>

          {/* 已掌握 */}
          <div className="glass rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold text-green-400 mb-1">
              {stats.learnedCards}
            </div>
            <div className="text-xs text-white/60">已掌握</div>
          </div>

          {/* 学习率 */}
          <div className="glass rounded-2xl p-4 text-center">
            <div className="text-2xl font-bold text-[#22a9d9] mb-1">
              {stats.totalCards > 0 
                ? Math.round((stats.learnedCards / stats.totalCards) * 100)
                : 0}%
            </div>
            <div className="text-xs text-white/60">掌握率</div>
          </div>
        </div>
      </div>

      {/* 快捷操作 */}
      <div className="px-6 mb-6">
        <button
          onClick={handleRandomStudy}
          disabled={stats.totalCards === 0}
          className="w-full h-32 rounded-2xl bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] p-6 flex items-center justify-between hover:shadow-glow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="text-left">
            <div className="flex items-center gap-2 mb-2">
              <Shuffle className="w-6 h-6 text-white" />
              <h3 className="text-xl font-bold text-white">随机复习</h3>
            </div>
            <p className="text-white/80 text-sm">
              {stats.totalCards > 0 
                ? `从 ${stats.totalCards} 张卡片中随机学习`
                : '暂无卡片,请先上传书籍'}
            </p>
          </div>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
        </button>
      </div>

      {/* 按书学习 */}
      <div className="px-6">
        <h2 className="text-lg font-bold text-white mb-4">按书学习</h2>
        
        {loading ? (
          <div className="text-center py-12 text-white/60">
            加载中...
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 mb-6">还没有书籍</p>
            <button
              onClick={() => setLocation('/upload')}
              className="px-8 h-12 rounded-xl bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] text-white font-medium hover:shadow-glow transition-shadow"
            >
              上传第一本书
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {books.map((book) => (
              <div
                key={book.id}
                className="glass rounded-2xl p-4 flex items-center gap-4"
              >
                {/* 书籍图标 */}
                <div className="w-12 h-16 rounded-xl bg-gradient-to-br from-[#22a9d9] to-[#6c5ce7] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>

                {/* 书籍信息 */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium truncate mb-1">
                    {book.title}
                  </h3>
                  <p className="text-white/60 text-sm mb-2">{book.author}</p>
                  
                  {/* 进度条 */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] transition-all"
                        style={{
                          width: `${book.card_count > 0 
                            ? (book.learned_count / book.card_count) * 100 
                            : 0}%`
                        }}
                      />
                    </div>
                    <span className="text-xs text-white/60">
                      {book.learned_count}/{book.card_count}
                    </span>
                  </div>
                </div>

                {/* 学习按钮 */}
                <button
                  onClick={() => handleBookStudy(book.id)}
                  disabled={book.card_count === 0}
                  className="px-6 h-10 rounded-xl bg-[#22a9d9]/20 text-[#22a9d9] font-medium hover:bg-[#22a9d9]/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  学习
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
